import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { Action } from '../../models/core/action';
import { isAuthTokenExpired } from '../../functions/is-auth-token-expired';
import { loginDeepLinkQueryParams } from '../../functions/login-navigate-to';
import { MessageBus } from '../../services/message/message-bus.service';
import { BaseComponent } from '../base/base.component';
import { ButtonComponent } from '../core/buttons/button.component';
import { RadioButtonComponent } from '../core/controls/radio-button.component';
import { ActionRowComponent } from '../core/panels/action-row.component';
import { ApiErrorLabelComponent } from '../core/labels/api-error-label.component';
import { RequiredFieldsNoteComponent } from '../core/labels/required-fields-note.component';

@Component({
  selector: 'app-mfa',
  imports: [
    ButtonComponent,
    RouterModule,
    ActionRowComponent,
    RadioButtonComponent,
    ApiErrorLabelComponent,
    RequiredFieldsNoteComponent,
  ],
  template: `
    <div class="tw-px-4 tw-pt-4 tw-pb-2 ">
      @if (error) {
        <div class="tw-flex tw-justify-center tw-pt-4 tw-pb-2">
          <app-api-error-label [error]="error" />
        </div>
      }
      <div class="tw-font-bold tw-py-4">Authentication required</div>
      <div>We need to confirm your identity before you can proceed.</div>

      <app-required-fields-note />
      <div class="tw-pt-6 tw-pb-8">
        <app-radio-button
          [control]="methodGroup.controls.method"
          label="Send verification code to"
          optionLabel="label"
          optionValue="key"
          [options]="methods"
          [helpText]="
            this.phone.length === 0 ? '' : 'Standard text message rates apply'
          "
        >
          <ng-template #label let-data>
            <div class="tw-ml-2 tw-mt-0.5 tw-mb-4 ">
              @if (data.key.startsWith('email')) {
                <div><b>Email</b>: {{ data.label }}</div>
              } @else if (data.key.startsWith('sms')) {
                <b>Text Message</b>: {{ data.label }}
              }
            </div>
          </ng-template>
        </app-radio-button>
      </div>
    </div>
    <div class="tw-flex tw-justify-end tw-pr-7 tw-pb-10">
      <app-action-row>
        <app-button
          (click)="sendCode()"
          right2
          label="Send Code"
          [disabled]="!this.methodGroup.valid"
          [loading]="loading"
        />
        <app-button routerLink="/login" emphasis="Low" right3 label="Cancel" />
      </app-action-row>
    </div>
  `,
  styles: ``,
})
export class MfaComponent extends BaseComponent implements OnInit {
  protected messagBus = inject(MessageBus);
  private route = inject(ActivatedRoute);

  token: string;
  expiresAt: string;
  phone: string[] = [];
  email: string[] = [];
  navigateTo: string;
  recordSource: string;
  recordId: string;
  loading = false;
  error: any;

  methods: Action[] = [
    { label: 'Email', key: 'email' },
    { label: 'Text Message', key: 'sms' },
  ];

  methodGroup = new FormGroup({
    method: new FormControl<string>(null, Validators.required),
  });

  ngOnInit(): void {
    const mfa: any = history.state.mfa;
    if (!mfa) {
      this.router.navigate(['/login']);
      return;
    }
    this.token = mfa.token;
    this.expiresAt = mfa.expiresAt;
    this.email = mfa.email.split(',');
    this.phone = mfa.phone ? mfa.phone.split(',') : [];

    this.methods = this.email.map((email, index) => ({
      label: email,
      key: `email-${index}`,
    }));

    const phoneMethods = this.phone.map((phone, index) => ({
      label: phone,
      key: `sms-${index}`,
    }));

    this.methods = [...this.methods, ...phoneMethods];

    if (this.methods.length === 1) {
      this.methodGroup.setValue({ method: this.methods[0].key });
    }

    this.navigateTo = history.state.navigateTo;
    this.recordSource = history.state.recordSource;
    this.recordId = history.state.recordId;
  }

  private navigateMfaExpired() {
    this.router.navigate(['/login'], {
      queryParams: { mfaExpired: true },
    });
  }

  async sendCode() {
    if (isAuthTokenExpired(this.expiresAt)) {
      this.navigateMfaExpired();
      return;
    }

    this.loading = true;
    try {
      const mfaResult = await this.api.auth.sendMfaCode({
        contactMethod: this.methodGroup.value.method,
        token: this.token,
      });
      if (mfaResult.tokenExpired) {
        this.navigateMfaExpired();
        return;
      }
      this.router.navigate(['/login/otp'], {
        queryParams: {
          profile: 'mfa',
          ...loginDeepLinkQueryParams({
            navigateTo: this.route.snapshot.queryParamMap.get('navigateto'),
            reportCode: this.route.snapshot.queryParamMap.get('reportcode'),
            recordSource: this.route.snapshot.queryParamMap.get('recordSource'),
            recordId: this.route.snapshot.queryParamMap.get('recordId'),
          }),
        },
        state: {
          otp: mfaResult,
          navigateTo: this.navigateTo,
          recordSource: this.recordSource,
          recordId: this.recordId,
        },
      });
    } catch (e) {
      this.error = e;
      if (this.error.error.type == 'SecurityTokenExpiredException') {
        this.navigateMfaExpired();
      }
    } finally {
      this.loading = false;
    }
  }
}
